import type { AnswerValue } from '../types';
import { CATEGORIES } from '../data/questions';
import type { SectionInfo } from '../lib/categoryUtils';

interface UnansweredSummaryProps {
  sections: SectionInfo[];
  answers: Record<string, AnswerValue>;
  onJumpToQuestion: (globalIndex: number) => void;
}

export default function UnansweredSummary({
  sections,
  answers,
  onJumpToQuestion,
}: UnansweredSummaryProps) {
  const sectionsWithGaps = sections
    .map((section) => {
      const unanswered = section.questions
        .map((q, i) => ({ question: q, localIndex: i }))
        .filter(({ question }) => !(question.id in answers));
      return { section, unanswered };
    })
    .filter(({ unanswered }) => unanswered.length > 0);

  if (sectionsWithGaps.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 py-3 text-green-600">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <span className="text-sm font-medium">All questions answered</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        Unanswered questions won't count as matches. Tap any to jump there.
      </p>
      {sectionsWithGaps.map(({ section, unanswered }) => {
        const meta = CATEGORIES.find((c) => c.id === section.category);
        if (!meta) return null;

        return (
          <div key={section.category}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-sm">{meta.emoji}</span>
              <span className="text-sm font-medium text-slate-700">
                {meta.label}
              </span>
              <span className="text-xs text-amber-500 font-medium">
                {unanswered.length} unanswered
              </span>
            </div>
            <div className="flex flex-col gap-1 pl-6">
              {unanswered.map(({ question, localIndex }) => (
                <button
                  key={question.id}
                  onClick={() => onJumpToQuestion(section.startIndex + localIndex)}
                  className="text-left text-sm text-primary-500 hover:text-primary-400 transition-colors truncate"
                >
                  {question.text}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
